/**
 * Image generation model definitions.
 *
 * Architecture:
 * - PromptModelKey ("brand"): stored in imagePrompt.model — groups prompts by brand
 * - GenerationModel: stored in generationHistory.model — the actual model used to generate
 * - Each brand has a default (latest) model for seeding the generate page
 * - Each model declares its supported capabilities (quality, aspect ratios, providers)
 */

import { creditsFromKieCredits } from '@/config/credit-economics';

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export enum GenerationModel {
  GROK_IMAGINE = 'grok-imagine',
  GPT_IMAGE_2_5 = 'gpt-image-2.5',
  GPT_IMAGE_2_5_SUNBURST = 'gpt-image-2.5-sunburst',
  GPT_IMAGE_2 = 'gpt-image-2',
  NANO_BANANA_PRO = 'nano-banana-pro',
  SEEDREAM_5_PRO = 'seedream-5-pro',
  SEEDREAM_4_5 = 'seedream-4.5',
  FLUX_2 = 'flux-2',
  NANO_BANANA = 'nano-banana',
  SEEDREAM_API = 'seedream-api',
  FOUR_O_IMAGE = '4o-image',
  FLUX_1_KONTEXT = 'flux-1-kontext',
  IMAGEN_4 = 'imagen-4',
  IDEOGRAM_V3 = 'ideogram-v3',
  IDEOGRAM_CHARACTER = 'ideogram-character',
  QWEN_IMAGE_EDIT = 'qwen-image-edit',
  Z_IMAGE = 'z-image',
  NANO_BANANA_2 = 'nano-banana-2',
  TOPAZ_IMAGE_UPSCALE = 'topaz-image-upscale',
}

export type ImageGenerationWorkflowType = 'image-generator' | 'image-editor';

export enum GenerationResolution {
  ONE_K = '1k',
  TWO_K = '2k',
  FOUR_K = '4k',
}

export enum GenerationQuality {
  BASIC = 'basic',
  STANDARD = 'standard',
  MEDIUM = 'medium',
  HIGH = 'high',
}

export enum GenerationBackground {
  TRANSPARENT = 'transparent',
  OPAQUE = 'opaque',
  AUTO = 'auto',
}

export enum GenerationAspectRatio {
  AUTO = 'auto',
  ONE_TO_ONE = '1:1',
  ONE_TO_FOUR = '1:4',
  ONE_TO_EIGHT = '1:8',
  ONE_TO_TWO = '1:2',
  ONE_TO_THREE = '1:3',
  TWO_TO_THREE = '2:3',
  TWO_TO_ONE = '2:1',
  THREE_TO_TWO = '3:2',
  THREE_TO_ONE = '3:1',
  THREE_TO_FOUR = '3:4',
  FOUR_TO_ONE = '4:1',
  FOUR_TO_THREE = '4:3',
  FOUR_TO_FIVE = '4:5',
  FIVE_TO_FOUR = '5:4',
  EIGHT_TO_NINE = '8:9',
  EIGHT_TO_ONE = '8:1',
  NINE_TO_EIGHT = '9:8',
  NINE_TO_SIXTEEN = '9:16',
  NINE_TO_TWENTY_ONE = '9:21',
  SIXTEEN_TO_NINE = '16:9',
  SIXTEEN_TO_TWENTY_SEVEN = '16:27',
  TWENTY_ONE_TO_NINE = '21:9',
  TWENTY_SEVEN_TO_SIXTEEN = '27:16',
}

export enum GenerationImageSize {
  SQUARE = 'square',
  SQUARE_HD = 'square_hd',
  PORTRAIT_4_3 = 'portrait_4_3',
  PORTRAIT_16_9 = 'portrait_16_9',
  LANDSCAPE_4_3 = 'landscape_4_3',
  LANDSCAPE_16_9 = 'landscape_16_9',
}

export enum ErrorCategory {
  CONTENT_POLICY = 'content_policy',
  INSUFFICIENT_CREDITS = 'insufficient_credits',
  PROVIDER_TIMEOUT = 'provider_timeout',
  PROVIDER_ERROR = 'provider_error',
  RATE_LIMIT = 'rate_limit',
  NETWORK_ERROR = 'network_error',
  R2_ERROR = 'r2_error',
  UNKNOWN = 'unknown',
}

// ---------------------------------------------------------------------------
// Brand (prompt model key)
// ---------------------------------------------------------------------------

/** Prompt-model code stored in imported prompt records. */
export type PromptModelKey = 'gptimage' | 'nanobanana';

export interface BrandConfig {
  key: PromptModelKey;
  displayName: string;
  defaultModel: GenerationModel;
}

export const BRANDS: Record<PromptModelKey, BrandConfig> = {
  nanobanana: {
    key: 'nanobanana',
    displayName: 'Nano Banana',
    defaultModel: GenerationModel.NANO_BANANA_PRO,
  },
  gptimage: {
    key: 'gptimage',
    displayName: 'GPT Image',
    defaultModel: GenerationModel.GPT_IMAGE_2_5,
  },
};

// ---------------------------------------------------------------------------
// Credit pricing
// ref: https://www.meigen.ai/model-comparison
// ---------------------------------------------------------------------------

/**
 * Flat credit cost per image for models that don't vary by resolution/quality.
 * These are legacy frontend credits (old economy multiplier was 5). The
 * charge path converts them through `creditsFromKieCredits`.
 */
export const FLAT_CREDIT_COST: Partial<Record<GenerationModel, number>> = {
  [GenerationModel.GROK_IMAGINE]: 20,
  [GenerationModel.SEEDREAM_5_PRO]: 12,
  [GenerationModel.SEEDREAM_4_5]: 12,
  [GenerationModel.FLUX_2]: 12,
  [GenerationModel.NANO_BANANA]: 40,
  [GenerationModel.SEEDREAM_API]: 10,
  [GenerationModel.FOUR_O_IMAGE]: 10,
  [GenerationModel.FLUX_1_KONTEXT]: 10,
  [GenerationModel.IMAGEN_4]: 10,
  [GenerationModel.IDEOGRAM_V3]: 10,
  [GenerationModel.IDEOGRAM_CHARACTER]: 10,
  [GenerationModel.QWEN_IMAGE_EDIT]: 10,
  [GenerationModel.Z_IMAGE]: 10,
  [GenerationModel.TOPAZ_IMAGE_UPSCALE]: 20,
};

const IMAGE_KIE_FLAT_COST: Partial<Record<GenerationModel, number>> = {
  [GenerationModel.NANO_BANANA]: 8,
};

const GROK_IMAGINE_KIE_CREDIT_TABLE: Record<GenerationQuality, number> = {
  [GenerationQuality.BASIC]: 4,
  [GenerationQuality.STANDARD]: 4,
  [GenerationQuality.MEDIUM]: 5,
  [GenerationQuality.HIGH]: 5,
};

const IMAGE_KIE_CREDIT_TABLE: Partial<
  Record<GenerationModel, Partial<Record<GenerationResolution, number>>>
> = {
  [GenerationModel.GPT_IMAGE_2]: {
    [GenerationResolution.ONE_K]: 6,
    [GenerationResolution.TWO_K]: 10,
    [GenerationResolution.FOUR_K]: 16,
  },
  [GenerationModel.GPT_IMAGE_2_5]: {
    [GenerationResolution.ONE_K]: 6,
    [GenerationResolution.TWO_K]: 10,
    [GenerationResolution.FOUR_K]: 16,
  },
  [GenerationModel.GPT_IMAGE_2_5_SUNBURST]: {
    [GenerationResolution.ONE_K]: 6,
    [GenerationResolution.TWO_K]: 10,
    [GenerationResolution.FOUR_K]: 16,
  },
  [GenerationModel.NANO_BANANA_2]: {
    [GenerationResolution.ONE_K]: 8,
    [GenerationResolution.TWO_K]: 12,
    [GenerationResolution.FOUR_K]: 18,
  },
  [GenerationModel.NANO_BANANA_PRO]: {
    [GenerationResolution.ONE_K]: 18,
    [GenerationResolution.TWO_K]: 18,
    [GenerationResolution.FOUR_K]: 24,
  },
};

export const IMAGE_CREDIT_TABLE: Partial<
  Record<GenerationModel, Partial<Record<GenerationResolution, number>>>
> = {
  [GenerationModel.GPT_IMAGE_2]: {
    [GenerationResolution.ONE_K]: 25,
    [GenerationResolution.TWO_K]: 40,
    [GenerationResolution.FOUR_K]: 65,
  },
  [GenerationModel.GPT_IMAGE_2_5]: {
    [GenerationResolution.ONE_K]: 25,
    [GenerationResolution.TWO_K]: 40,
    [GenerationResolution.FOUR_K]: 65,
  },
  [GenerationModel.GPT_IMAGE_2_5_SUNBURST]: {
    [GenerationResolution.ONE_K]: 25,
    [GenerationResolution.TWO_K]: 40,
    [GenerationResolution.FOUR_K]: 65,
  },
  [GenerationModel.NANO_BANANA_2]: {
    [GenerationResolution.ONE_K]: 30,
    [GenerationResolution.TWO_K]: 50,
    [GenerationResolution.FOUR_K]: 70,
  },
  [GenerationModel.NANO_BANANA_PRO]: {
    [GenerationResolution.ONE_K]: 45,
    [GenerationResolution.TWO_K]: 45,
    [GenerationResolution.FOUR_K]: 60,
  },
};

/**
 * GPT Image 2 credit cost lookup table.
 * Indexed by [resolution][quality].
 */
export const GPT_IMAGE_2_CREDIT_TABLE: Record<
  GenerationResolution,
  Partial<Record<GenerationQuality, number>>
> = {
  [GenerationResolution.ONE_K]: {
    [GenerationQuality.STANDARD]: 25,
    [GenerationQuality.MEDIUM]: 25,
    [GenerationQuality.HIGH]: 25,
  },
  [GenerationResolution.TWO_K]: {
    [GenerationQuality.STANDARD]: 40,
    [GenerationQuality.MEDIUM]: 40,
    [GenerationQuality.HIGH]: 40,
  },
  [GenerationResolution.FOUR_K]: {
    [GenerationQuality.STANDARD]: 65,
    [GenerationQuality.MEDIUM]: 65,
    [GenerationQuality.HIGH]: 65,
  },
};

export function getImageOutputLimit(
  model: GenerationModel | string,
  quality?: GenerationQuality | string,
  referenceImageCount = 0
): number {
  if (model !== GenerationModel.GROK_IMAGINE || referenceImageCount > 0) {
    return 1;
  }

  return quality === GenerationQuality.HIGH ||
    quality === GenerationQuality.MEDIUM
    ? 4
    : 6;
}

export function calculateGenerationCost(
  resolution: GenerationResolution | string,
  model: GenerationModel | string,
  quality?: GenerationQuality | string
): number {
  if (model === GenerationModel.GROK_IMAGINE) {
    const grokQuality =
      (quality as GenerationQuality | undefined) ?? GenerationQuality.STANDARD;
    return creditsFromKieCredits(
      GROK_IMAGINE_KIE_CREDIT_TABLE[grokQuality] ??
        GROK_IMAGINE_KIE_CREDIT_TABLE[GenerationQuality.STANDARD]
    );
  }

  const kieTableCost =
    IMAGE_KIE_CREDIT_TABLE[model as GenerationModel]?.[
      resolution as GenerationResolution
    ];
  if (kieTableCost !== undefined) {
    return creditsFromKieCredits(kieTableCost);
  }

  const kieFlatCost = IMAGE_KIE_FLAT_COST[model as GenerationModel];
  if (kieFlatCost !== undefined) {
    return creditsFromKieCredits(kieFlatCost);
  }

  const tableCost =
    IMAGE_CREDIT_TABLE[model as GenerationModel]?.[
      resolution as GenerationResolution
    ];
  if (tableCost !== undefined) {
    return creditsFromLegacyFrontendCredits(tableCost);
  }

  const flatCost = FLAT_CREDIT_COST[model as GenerationModel];
  if (flatCost !== undefined) {
    return creditsFromLegacyFrontendCredits(flatCost);
  }

  // Legacy quality table fallback for models that re-enable quality tiers.
  if (model === GenerationModel.GPT_IMAGE_2) {
    const res =
      (resolution as GenerationResolution) || GenerationResolution.TWO_K;
    const qual = (quality as GenerationQuality) || GenerationQuality.STANDARD;
    return creditsFromLegacyFrontendCredits(
      GPT_IMAGE_2_CREDIT_TABLE[res]?.[qual] ?? 30
    );
  }

  return creditsFromKieCredits(2.5);
}

/** Old image frontend credits used an economy multiplier of 5. */
function creditsFromLegacyFrontendCredits(frontendCredits: number): number {
  return creditsFromKieCredits(frontendCredits / 5);
}

export function getMinimumGenerationCost(
  model: GenerationModel | string
): number {
  const config = getModelConfig(model as GenerationModel);
  if (!config) {
    return calculateGenerationCost(GenerationResolution.ONE_K, model);
  }

  const qualities =
    config.supportedQualities.length > 0
      ? config.supportedQualities
      : [undefined];
  const costs = config.supportedResolutions.flatMap((resolution) =>
    qualities.map((quality) =>
      calculateGenerationCost(resolution, config.id, quality)
    )
  );

  return Math.min(...costs);
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

export const RESOLUTION_DISPLAY: Record<
  GenerationResolution,
  { label: string }
> = {
  [GenerationResolution.ONE_K]: { label: '1K' },
  [GenerationResolution.TWO_K]: { label: '2K' },
  [GenerationResolution.FOUR_K]: { label: '4K' },
};

export const QUALITY_DISPLAY: Record<GenerationQuality, { label: string }> = {
  [GenerationQuality.BASIC]: { label: 'Basic' },
  [GenerationQuality.STANDARD]: { label: 'Standard' },
  [GenerationQuality.MEDIUM]: { label: 'Medium' },
  [GenerationQuality.HIGH]: { label: 'Quality' },
};

export const BACKGROUND_DISPLAY: Record<
  GenerationBackground,
  { label: string }
> = {
  [GenerationBackground.TRANSPARENT]: { label: 'Transparent' },
  [GenerationBackground.OPAQUE]: { label: 'Opaque' },
  [GenerationBackground.AUTO]: { label: 'Auto' },
};

export const ASPECT_RATIO_DISPLAY: Record<
  GenerationAspectRatio,
  { label: string }
> = {
  [GenerationAspectRatio.AUTO]: { label: 'Auto' },
  [GenerationAspectRatio.ONE_TO_ONE]: { label: '1:1' },
  [GenerationAspectRatio.ONE_TO_FOUR]: { label: '1:4' },
  [GenerationAspectRatio.ONE_TO_EIGHT]: { label: '1:8' },
  [GenerationAspectRatio.ONE_TO_TWO]: { label: '1:2' },
  [GenerationAspectRatio.ONE_TO_THREE]: { label: '1:3' },
  [GenerationAspectRatio.TWO_TO_THREE]: { label: '2:3' },
  [GenerationAspectRatio.TWO_TO_ONE]: { label: '2:1' },
  [GenerationAspectRatio.THREE_TO_TWO]: { label: '3:2' },
  [GenerationAspectRatio.THREE_TO_ONE]: { label: '3:1' },
  [GenerationAspectRatio.THREE_TO_FOUR]: { label: '3:4' },
  [GenerationAspectRatio.FOUR_TO_ONE]: { label: '4:1' },
  [GenerationAspectRatio.FOUR_TO_THREE]: { label: '4:3' },
  [GenerationAspectRatio.FOUR_TO_FIVE]: { label: '4:5' },
  [GenerationAspectRatio.FIVE_TO_FOUR]: { label: '5:4' },
  [GenerationAspectRatio.EIGHT_TO_NINE]: { label: '8:9' },
  [GenerationAspectRatio.EIGHT_TO_ONE]: { label: '8:1' },
  [GenerationAspectRatio.NINE_TO_EIGHT]: { label: '9:8' },
  [GenerationAspectRatio.NINE_TO_SIXTEEN]: { label: '9:16' },
  [GenerationAspectRatio.NINE_TO_TWENTY_ONE]: { label: '9:21' },
  [GenerationAspectRatio.SIXTEEN_TO_NINE]: { label: '16:9' },
  [GenerationAspectRatio.SIXTEEN_TO_TWENTY_SEVEN]: { label: '16:27' },
  [GenerationAspectRatio.TWENTY_ONE_TO_NINE]: { label: '21:9' },
  [GenerationAspectRatio.TWENTY_SEVEN_TO_SIXTEEN]: { label: '27:16' },
};

export const IMAGE_SIZE_DISPLAY: Record<
  GenerationImageSize,
  { label: string }
> = {
  [GenerationImageSize.SQUARE]: { label: 'Square' },
  [GenerationImageSize.SQUARE_HD]: { label: 'Square HD' },
  [GenerationImageSize.PORTRAIT_4_3]: { label: '3:4' },
  [GenerationImageSize.PORTRAIT_16_9]: { label: '9:16' },
  [GenerationImageSize.LANDSCAPE_4_3]: { label: '4:3' },
  [GenerationImageSize.LANDSCAPE_16_9]: { label: '16:9' },
};

export const ERROR_CATEGORY_DISPLAY: Record<ErrorCategory, { label: string }> =
  {
    [ErrorCategory.CONTENT_POLICY]: { label: 'Content Policy' },
    [ErrorCategory.INSUFFICIENT_CREDITS]: { label: 'Insufficient Credits' },
    [ErrorCategory.PROVIDER_TIMEOUT]: { label: 'Provider Timeout' },
    [ErrorCategory.PROVIDER_ERROR]: { label: 'Provider Error' },
    [ErrorCategory.RATE_LIMIT]: { label: 'Rate Limit' },
    [ErrorCategory.NETWORK_ERROR]: { label: 'Network Error' },
    [ErrorCategory.R2_ERROR]: { label: 'R2 Error' },
    [ErrorCategory.UNKNOWN]: { label: 'Unknown' },
  };

// ---------------------------------------------------------------------------
// Model configuration
// ---------------------------------------------------------------------------

export type ImageProvider = 'fal' | 'apimart' | 'kie';

export const PROVIDER_DISPLAY: Record<ImageProvider, { label: string }> = {
  fal: { label: 'fal.ai' },
  apimart: { label: 'APIMart' },
  kie: { label: 'KIE' },
};

export interface ModelConfig {
  id: GenerationModel;
  displayName: string;
  description: string;
  promptKey: PromptModelKey;
  providers: ImageProvider[];
  preferredProvider: ImageProvider;
  supportedResolutions: GenerationResolution[];
  supportedQualities: GenerationQuality[];
  supportedAspectRatios: GenerationAspectRatio[];
  supportedImageSizes?: GenerationImageSize[];
  supportedBackgrounds?: GenerationBackground[];
  defaultResolution?: GenerationResolution;
  defaultQuality?: GenerationQuality;
  defaultAspectRatio?: GenerationAspectRatio;
  defaultImageSize?: GenerationImageSize;
  defaultBackground?: GenerationBackground;
  supportsReferenceImages?: boolean;
  requiresReferenceImages?: boolean;
  maxReferenceImages?: number;
  maxPromptLength?: number;
  kieEndpoint?: 'market' | 'gpt4o' | 'flux-kontext';
  kieModelId?: string;
  kieTextModelId?: string;
  kieImageModelId?: string;
}

const CORE_ASPECT_RATIOS = [
  GenerationAspectRatio.ONE_TO_ONE,
  GenerationAspectRatio.TWO_TO_THREE,
  GenerationAspectRatio.THREE_TO_TWO,
  GenerationAspectRatio.THREE_TO_FOUR,
  GenerationAspectRatio.FOUR_TO_THREE,
  GenerationAspectRatio.NINE_TO_SIXTEEN,
  GenerationAspectRatio.SIXTEEN_TO_NINE,
];

const KIE_EXTENDED_ASPECT_RATIOS = [
  GenerationAspectRatio.ONE_TO_ONE,
  GenerationAspectRatio.NINE_TO_SIXTEEN,
  GenerationAspectRatio.SIXTEEN_TO_NINE,
  GenerationAspectRatio.THREE_TO_FOUR,
  GenerationAspectRatio.FOUR_TO_THREE,
  GenerationAspectRatio.THREE_TO_TWO,
  GenerationAspectRatio.TWO_TO_THREE,
  GenerationAspectRatio.FIVE_TO_FOUR,
  GenerationAspectRatio.FOUR_TO_FIVE,
  GenerationAspectRatio.TWENTY_ONE_TO_NINE,
  GenerationAspectRatio.AUTO,
];

const NANO_BANANA_2_ASPECT_RATIOS = [
  GenerationAspectRatio.ONE_TO_ONE,
  GenerationAspectRatio.ONE_TO_FOUR,
  GenerationAspectRatio.ONE_TO_EIGHT,
  GenerationAspectRatio.TWO_TO_THREE,
  GenerationAspectRatio.THREE_TO_TWO,
  GenerationAspectRatio.THREE_TO_FOUR,
  GenerationAspectRatio.FOUR_TO_ONE,
  GenerationAspectRatio.FOUR_TO_THREE,
  GenerationAspectRatio.FOUR_TO_FIVE,
  GenerationAspectRatio.FIVE_TO_FOUR,
  GenerationAspectRatio.EIGHT_TO_ONE,
  GenerationAspectRatio.NINE_TO_SIXTEEN,
  GenerationAspectRatio.SIXTEEN_TO_NINE,
  GenerationAspectRatio.TWENTY_ONE_TO_NINE,
  GenerationAspectRatio.AUTO,
];

const GPT_IMAGE_2_ASPECT_RATIOS = [
  GenerationAspectRatio.AUTO,
  GenerationAspectRatio.ONE_TO_ONE,
  GenerationAspectRatio.THREE_TO_TWO,
  GenerationAspectRatio.TWO_TO_THREE,
  GenerationAspectRatio.FOUR_TO_THREE,
  GenerationAspectRatio.THREE_TO_FOUR,
  GenerationAspectRatio.FIVE_TO_FOUR,
  GenerationAspectRatio.FOUR_TO_FIVE,
  GenerationAspectRatio.SIXTEEN_TO_NINE,
  GenerationAspectRatio.NINE_TO_SIXTEEN,
  GenerationAspectRatio.TWO_TO_ONE,
  GenerationAspectRatio.ONE_TO_TWO,
  GenerationAspectRatio.THREE_TO_ONE,
  GenerationAspectRatio.ONE_TO_THREE,
  GenerationAspectRatio.TWENTY_ONE_TO_NINE,
  GenerationAspectRatio.NINE_TO_TWENTY_ONE,
];

const GPT_IMAGE_2_5_ASPECT_RATIOS = [
  GenerationAspectRatio.AUTO,
  GenerationAspectRatio.ONE_TO_ONE,
  GenerationAspectRatio.THREE_TO_TWO,
  GenerationAspectRatio.TWO_TO_THREE,
  GenerationAspectRatio.FOUR_TO_THREE,
  GenerationAspectRatio.THREE_TO_FOUR,
  GenerationAspectRatio.SIXTEEN_TO_NINE,
  GenerationAspectRatio.NINE_TO_SIXTEEN,
  GenerationAspectRatio.TWENTY_ONE_TO_NINE,
  GenerationAspectRatio.TWENTY_SEVEN_TO_SIXTEEN,
  GenerationAspectRatio.SIXTEEN_TO_TWENTY_SEVEN,
  GenerationAspectRatio.NINE_TO_EIGHT,
  GenerationAspectRatio.EIGHT_TO_NINE,
];

const GPT_IMAGE_2_5_BACKGROUNDS = [
  GenerationBackground.TRANSPARENT,
  GenerationBackground.OPAQUE,
  GenerationBackground.AUTO,
];

const FLUX_2_ASPECT_RATIOS = [
  GenerationAspectRatio.ONE_TO_ONE,
  GenerationAspectRatio.FOUR_TO_THREE,
  GenerationAspectRatio.THREE_TO_FOUR,
  GenerationAspectRatio.SIXTEEN_TO_NINE,
  GenerationAspectRatio.NINE_TO_SIXTEEN,
  GenerationAspectRatio.THREE_TO_TWO,
  GenerationAspectRatio.TWO_TO_THREE,
];

const FLUX_KONTEXT_ASPECT_RATIOS = [
  GenerationAspectRatio.TWENTY_ONE_TO_NINE,
  GenerationAspectRatio.SIXTEEN_TO_NINE,
  GenerationAspectRatio.FOUR_TO_THREE,
  GenerationAspectRatio.ONE_TO_ONE,
  GenerationAspectRatio.THREE_TO_FOUR,
  GenerationAspectRatio.NINE_TO_SIXTEEN,
];

const IMAGE_SIZE_OPTIONS = [
  GenerationImageSize.SQUARE,
  GenerationImageSize.SQUARE_HD,
  GenerationImageSize.PORTRAIT_4_3,
  GenerationImageSize.PORTRAIT_16_9,
  GenerationImageSize.LANDSCAPE_4_3,
  GenerationImageSize.LANDSCAPE_16_9,
];

export const IMAGE_SIZE_ASPECT_RATIO: Record<
  GenerationImageSize,
  GenerationAspectRatio
> = {
  [GenerationImageSize.SQUARE]: GenerationAspectRatio.ONE_TO_ONE,
  [GenerationImageSize.SQUARE_HD]: GenerationAspectRatio.ONE_TO_ONE,
  [GenerationImageSize.PORTRAIT_4_3]: GenerationAspectRatio.THREE_TO_FOUR,
  [GenerationImageSize.PORTRAIT_16_9]: GenerationAspectRatio.NINE_TO_SIXTEEN,
  [GenerationImageSize.LANDSCAPE_4_3]: GenerationAspectRatio.FOUR_TO_THREE,
  [GenerationImageSize.LANDSCAPE_16_9]: GenerationAspectRatio.SIXTEEN_TO_NINE,
};

export function getImageSizeAspectRatio(
  imageSize: GenerationImageSize
): GenerationAspectRatio {
  return IMAGE_SIZE_ASPECT_RATIO[imageSize];
}

export function getDefaultImageSizeForAspectRatio(
  aspectRatio: GenerationAspectRatio
): GenerationImageSize | undefined {
  return IMAGE_SIZE_OPTIONS.find(
    (size) => IMAGE_SIZE_ASPECT_RATIO[size] === aspectRatio
  );
}

export const GROK_IMAGINE_CONFIG: ModelConfig = {
  id: GenerationModel.GROK_IMAGINE,
  displayName: 'Grok Imagine',
  description:
    'Default Grok image workflow on KIE for text or reference edits.',
  promptKey: 'nanobanana',
  providers: ['kie'],
  preferredProvider: 'kie',
  supportedResolutions: [GenerationResolution.ONE_K],
  supportedQualities: [GenerationQuality.STANDARD, GenerationQuality.HIGH],
  supportedAspectRatios: CORE_ASPECT_RATIOS,
  defaultAspectRatio: GenerationAspectRatio.ONE_TO_ONE,
  defaultResolution: GenerationResolution.ONE_K,
  defaultQuality: GenerationQuality.STANDARD,
  supportsReferenceImages: true,
  maxReferenceImages: 5,
  maxPromptLength: 5000,
  kieTextModelId: 'grok-imagine/text-to-image',
  kieImageModelId: 'grok-imagine/image-to-image',
};

/**
 * Nano Banana (Gemini 2.5 Flash Image Preview)
 * - No quality/resolution control (single tier)
 * - fal.ai only
 */
export const NANO_BANANA_CONFIG: ModelConfig = {
  id: GenerationModel.NANO_BANANA,
  displayName: 'Nano Banana',
  description: 'Fast Google image model from KIE. Aspect-ratio driven output.',
  promptKey: 'nanobanana',
  providers: ['kie'],
  preferredProvider: 'kie',
  supportedResolutions: [GenerationResolution.ONE_K],
  supportedQualities: [],
  supportedAspectRatios: KIE_EXTENDED_ASPECT_RATIOS,
  defaultAspectRatio: GenerationAspectRatio.ONE_TO_ONE,
  defaultResolution: GenerationResolution.ONE_K,
  maxPromptLength: 5000,
  kieModelId: 'google/nano-banana',
};

/**
 * Nano Banana 2 (Gemini 3.1 Flash Image Preview)
 * - Supports 0.5K/1K/2K/4K resolution
 * - fal.ai + APImart
 */
export const NANO_BANANA_2_CONFIG: ModelConfig = {
  id: GenerationModel.NANO_BANANA_2,
  displayName: 'Nano Banana 2',
  description:
    'Next-gen with reasoning-guided generation. Supports up to 4K resolution.',
  promptKey: 'nanobanana',
  providers: ['fal', 'apimart', 'kie'],
  preferredProvider: 'kie',
  supportedResolutions: [
    GenerationResolution.ONE_K,
    GenerationResolution.TWO_K,
    GenerationResolution.FOUR_K,
  ],
  supportedQualities: [],
  supportedAspectRatios: NANO_BANANA_2_ASPECT_RATIOS,
  defaultAspectRatio: GenerationAspectRatio.AUTO,
  defaultResolution: GenerationResolution.ONE_K,
  supportsReferenceImages: true,
  maxReferenceImages: 14,
  maxPromptLength: 20000,
  kieModelId: 'nano-banana-2',
};

/**
 * Nano Banana Pro (Gemini 3 Pro Image Preview)
 * - Supports 1K/2K/4K resolution (no 0.5K)
 * - fal.ai + APImart
 */
export const NANO_BANANA_PRO_CONFIG: ModelConfig = {
  id: GenerationModel.NANO_BANANA_PRO,
  displayName: 'Nano Banana Pro',
  description: 'Highest quality Google model. Supports up to 4K resolution.',
  promptKey: 'nanobanana',
  providers: ['fal', 'apimart', 'kie'],
  preferredProvider: 'kie',
  supportedResolutions: [
    GenerationResolution.ONE_K,
    GenerationResolution.TWO_K,
    GenerationResolution.FOUR_K,
  ],
  supportedQualities: [],
  supportedAspectRatios: CORE_ASPECT_RATIOS,
  defaultAspectRatio: GenerationAspectRatio.ONE_TO_ONE,
  defaultResolution: GenerationResolution.ONE_K,
  supportsReferenceImages: true,
  maxReferenceImages: 8,
  maxPromptLength: 5000,
  kieModelId: 'nano-banana-pro',
};

/**
 * GPT Image 2 (OpenAI GPT-Image-2)
 * - Supports quality (low/medium/high) and resolution (1K/2K/4K)
 * - Available on fal.ai, APImart, and KIE
 */
export const GPT_IMAGE_2_CONFIG: ModelConfig = {
  id: GenerationModel.GPT_IMAGE_2,
  displayName: 'GPT Image 2',
  description:
    'OpenAI image model on KIE with broad aspect-ratio and resolution support.',
  promptKey: 'gptimage',
  providers: ['fal', 'apimart', 'kie'],
  preferredProvider: 'kie',
  supportedResolutions: [
    GenerationResolution.ONE_K,
    GenerationResolution.TWO_K,
    GenerationResolution.FOUR_K,
  ],
  supportedQualities: [],
  supportedAspectRatios: GPT_IMAGE_2_ASPECT_RATIOS,
  defaultAspectRatio: GenerationAspectRatio.AUTO,
  defaultResolution: GenerationResolution.ONE_K,
  supportsReferenceImages: true,
  maxReferenceImages: 16,
  maxPromptLength: 20000,
  kieTextModelId: 'gpt-image-2-text-to-image',
  kieImageModelId: 'gpt-image-2-image-to-image',
};

/**
 * GPT Image 2.5 Flare (OpenAI GPT-Image-2.5)
 * - Faster default 2.5 variant for everyday generation and edits
 * - Supports 1K/2K/4K; 27:16, 16:27, 9:8, and 8:9 are 1K-only
 */
export const GPT_IMAGE_2_5_CONFIG: ModelConfig = {
  id: GenerationModel.GPT_IMAGE_2_5,
  displayName: 'GPT Image 2.5 Flare',
  description:
    'Faster GPT Image 2.5 on KIE for everyday generation and reference edits.',
  promptKey: 'gptimage',
  providers: ['kie'],
  preferredProvider: 'kie',
  supportedResolutions: [
    GenerationResolution.ONE_K,
    GenerationResolution.TWO_K,
    GenerationResolution.FOUR_K,
  ],
  supportedQualities: [],
  supportedAspectRatios: GPT_IMAGE_2_5_ASPECT_RATIOS,
  supportedBackgrounds: GPT_IMAGE_2_5_BACKGROUNDS,
  defaultAspectRatio: GenerationAspectRatio.AUTO,
  defaultResolution: GenerationResolution.ONE_K,
  defaultBackground: GenerationBackground.AUTO,
  supportsReferenceImages: true,
  maxReferenceImages: 16,
  maxPromptLength: 20000,
  kieTextModelId: 'gpt-image-2-5-flare-text-to-image',
  kieImageModelId: 'gpt-image-2-5-flare-image-to-image',
};

/**
 * GPT Image 2.5 Sunburst (OpenAI GPT-Image-2.5)
 * - Slower, higher-control 2.5 variant for precise edits
 * - Same KIE credit table and aspect-ratio rules as Flare
 */
export const GPT_IMAGE_2_5_SUNBURST_CONFIG: ModelConfig = {
  id: GenerationModel.GPT_IMAGE_2_5_SUNBURST,
  displayName: 'GPT Image 2.5 Sunburst',
  description:
    'Higher-control GPT Image 2.5 on KIE for precise, production-style edits.',
  promptKey: 'gptimage',
  providers: ['kie'],
  preferredProvider: 'kie',
  supportedResolutions: [
    GenerationResolution.ONE_K,
    GenerationResolution.TWO_K,
    GenerationResolution.FOUR_K,
  ],
  supportedQualities: [],
  supportedAspectRatios: GPT_IMAGE_2_5_ASPECT_RATIOS,
  supportedBackgrounds: GPT_IMAGE_2_5_BACKGROUNDS,
  defaultAspectRatio: GenerationAspectRatio.AUTO,
  defaultResolution: GenerationResolution.ONE_K,
  defaultBackground: GenerationBackground.AUTO,
  supportsReferenceImages: true,
  maxReferenceImages: 16,
  maxPromptLength: 20000,
  kieTextModelId: 'gpt-image-2-5-sunburst-text-to-image',
  kieImageModelId: 'gpt-image-2-5-sunburst-image-to-image',
};

export const SEEDREAM_4_5_CONFIG: ModelConfig = {
  id: GenerationModel.SEEDREAM_4_5,
  displayName: 'Seedream 4.5',
  description: 'Seedream 4.5 text and image editing with Basic/High output.',
  promptKey: 'nanobanana',
  providers: ['kie'],
  preferredProvider: 'kie',
  supportedResolutions: [GenerationResolution.ONE_K],
  supportedQualities: [GenerationQuality.BASIC, GenerationQuality.HIGH],
  supportedAspectRatios: [
    GenerationAspectRatio.ONE_TO_ONE,
    GenerationAspectRatio.FOUR_TO_THREE,
    GenerationAspectRatio.THREE_TO_FOUR,
    GenerationAspectRatio.SIXTEEN_TO_NINE,
    GenerationAspectRatio.NINE_TO_SIXTEEN,
    GenerationAspectRatio.TWO_TO_THREE,
    GenerationAspectRatio.THREE_TO_TWO,
    GenerationAspectRatio.TWENTY_ONE_TO_NINE,
  ],
  defaultAspectRatio: GenerationAspectRatio.ONE_TO_ONE,
  defaultResolution: GenerationResolution.ONE_K,
  defaultQuality: GenerationQuality.BASIC,
  supportsReferenceImages: true,
  maxReferenceImages: 14,
  maxPromptLength: 3000,
  kieTextModelId: 'seedream/4.5-text-to-image',
  kieImageModelId: 'seedream/4.5-edit',
};

export const SEEDREAM_5_PRO_CONFIG: ModelConfig = {
  id: GenerationModel.SEEDREAM_5_PRO,
  displayName: 'Seedream 5.0 Pro',
  description:
    'Seedream 5.0 Pro for dense visual prompts, precise edits, and realistic images.',
  promptKey: 'nanobanana',
  providers: ['kie'],
  preferredProvider: 'kie',
  supportedResolutions: [GenerationResolution.ONE_K],
  supportedQualities: [GenerationQuality.BASIC, GenerationQuality.HIGH],
  supportedAspectRatios: [
    GenerationAspectRatio.ONE_TO_ONE,
    GenerationAspectRatio.FOUR_TO_THREE,
    GenerationAspectRatio.THREE_TO_FOUR,
    GenerationAspectRatio.SIXTEEN_TO_NINE,
    GenerationAspectRatio.NINE_TO_SIXTEEN,
    GenerationAspectRatio.TWO_TO_THREE,
    GenerationAspectRatio.THREE_TO_TWO,
    GenerationAspectRatio.TWENTY_ONE_TO_NINE,
  ],
  defaultAspectRatio: GenerationAspectRatio.ONE_TO_ONE,
  defaultResolution: GenerationResolution.ONE_K,
  defaultQuality: GenerationQuality.BASIC,
  supportsReferenceImages: true,
  maxReferenceImages: 14,
  maxPromptLength: 3000,
  kieTextModelId: 'seedream/5.0-pro-text-to-image',
  kieImageModelId: 'seedream/5.0-pro-edit',
};

export const FLUX_2_CONFIG: ModelConfig = {
  id: GenerationModel.FLUX_2,
  displayName: 'Flux. 2',
  description: 'Flux 2 Pro image generation and reference-guided editing.',
  promptKey: 'nanobanana',
  providers: ['kie'],
  preferredProvider: 'kie',
  supportedResolutions: [
    GenerationResolution.ONE_K,
    GenerationResolution.TWO_K,
  ],
  supportedQualities: [],
  supportedAspectRatios: FLUX_2_ASPECT_RATIOS,
  defaultAspectRatio: GenerationAspectRatio.ONE_TO_ONE,
  defaultResolution: GenerationResolution.ONE_K,
  supportsReferenceImages: true,
  maxReferenceImages: 8,
  maxPromptLength: 5000,
  kieTextModelId: 'flux-2/pro-text-to-image',
  kieImageModelId: 'flux-2/pro-image-to-image',
};

export const SEEDREAM_API_CONFIG: ModelConfig = {
  id: GenerationModel.SEEDREAM_API,
  displayName: 'Seedream',
  description: 'Seedream image API with fixed image-size presets.',
  promptKey: 'nanobanana',
  providers: ['kie'],
  preferredProvider: 'kie',
  supportedResolutions: [GenerationResolution.ONE_K],
  supportedQualities: [],
  supportedAspectRatios: [
    GenerationAspectRatio.ONE_TO_ONE,
    GenerationAspectRatio.THREE_TO_FOUR,
    GenerationAspectRatio.NINE_TO_SIXTEEN,
    GenerationAspectRatio.FOUR_TO_THREE,
    GenerationAspectRatio.SIXTEEN_TO_NINE,
  ],
  supportedImageSizes: IMAGE_SIZE_OPTIONS,
  defaultImageSize: GenerationImageSize.SQUARE_HD,
  defaultAspectRatio: GenerationAspectRatio.ONE_TO_ONE,
  defaultResolution: GenerationResolution.ONE_K,
  maxPromptLength: 5000,
  kieModelId: 'bytedance/seedream',
};

export const FOUR_O_IMAGE_CONFIG: ModelConfig = {
  id: GenerationModel.FOUR_O_IMAGE,
  displayName: '4o Image',
  description: '4o image API with prompt or up to five reference images.',
  promptKey: 'gptimage',
  providers: ['kie'],
  preferredProvider: 'kie',
  supportedResolutions: [GenerationResolution.ONE_K],
  supportedQualities: [],
  supportedAspectRatios: [
    GenerationAspectRatio.ONE_TO_ONE,
    GenerationAspectRatio.THREE_TO_TWO,
    GenerationAspectRatio.TWO_TO_THREE,
  ],
  defaultAspectRatio: GenerationAspectRatio.ONE_TO_ONE,
  defaultResolution: GenerationResolution.ONE_K,
  supportsReferenceImages: true,
  maxReferenceImages: 5,
  maxPromptLength: 2000,
  kieEndpoint: 'gpt4o',
};

export const FLUX_1_KONTEXT_CONFIG: ModelConfig = {
  id: GenerationModel.FLUX_1_KONTEXT,
  displayName: 'Flux.1 Kontext',
  description: 'Flux Kontext generation and edit API with dedicated ratio set.',
  promptKey: 'nanobanana',
  providers: ['kie'],
  preferredProvider: 'kie',
  supportedResolutions: [GenerationResolution.ONE_K],
  supportedQualities: [],
  supportedAspectRatios: FLUX_KONTEXT_ASPECT_RATIOS,
  defaultAspectRatio: GenerationAspectRatio.SIXTEEN_TO_NINE,
  defaultResolution: GenerationResolution.ONE_K,
  supportsReferenceImages: true,
  maxReferenceImages: 1,
  maxPromptLength: 2000,
  kieEndpoint: 'flux-kontext',
  kieModelId: 'flux-kontext-pro',
};

export const IMAGEN_4_CONFIG: ModelConfig = {
  id: GenerationModel.IMAGEN_4,
  displayName: 'Imagen 4',
  description: 'Google Imagen 4 text-to-image with KIE aspect-ratio controls.',
  promptKey: 'nanobanana',
  providers: ['kie'],
  preferredProvider: 'kie',
  supportedResolutions: [GenerationResolution.ONE_K],
  supportedQualities: [],
  supportedAspectRatios: [
    GenerationAspectRatio.ONE_TO_ONE,
    GenerationAspectRatio.SIXTEEN_TO_NINE,
    GenerationAspectRatio.NINE_TO_SIXTEEN,
    GenerationAspectRatio.THREE_TO_FOUR,
    GenerationAspectRatio.FOUR_TO_THREE,
    GenerationAspectRatio.AUTO,
  ],
  defaultAspectRatio: GenerationAspectRatio.ONE_TO_ONE,
  defaultResolution: GenerationResolution.ONE_K,
  maxPromptLength: 5000,
  kieModelId: 'google/imagen4',
};

export const IDEOGRAM_V3_CONFIG: ModelConfig = {
  id: GenerationModel.IDEOGRAM_V3,
  displayName: 'Ideogram V3',
  description: 'Ideogram V3 text-to-image with real image-size presets.',
  promptKey: 'nanobanana',
  providers: ['kie'],
  preferredProvider: 'kie',
  supportedResolutions: [GenerationResolution.ONE_K],
  supportedQualities: [],
  supportedAspectRatios: [
    GenerationAspectRatio.ONE_TO_ONE,
    GenerationAspectRatio.THREE_TO_FOUR,
    GenerationAspectRatio.NINE_TO_SIXTEEN,
    GenerationAspectRatio.FOUR_TO_THREE,
    GenerationAspectRatio.SIXTEEN_TO_NINE,
  ],
  supportedImageSizes: IMAGE_SIZE_OPTIONS,
  defaultImageSize: GenerationImageSize.SQUARE_HD,
  defaultAspectRatio: GenerationAspectRatio.ONE_TO_ONE,
  defaultResolution: GenerationResolution.ONE_K,
  maxPromptLength: 5000,
  kieModelId: 'ideogram/v3-text-to-image',
};

export const IDEOGRAM_CHARACTER_CONFIG: ModelConfig = {
  id: GenerationModel.IDEOGRAM_CHARACTER,
  displayName: 'Ideogram Character',
  description: 'Ideogram character generation from one reference image.',
  promptKey: 'nanobanana',
  providers: ['kie'],
  preferredProvider: 'kie',
  supportedResolutions: [GenerationResolution.ONE_K],
  supportedQualities: [],
  supportedAspectRatios: [
    GenerationAspectRatio.ONE_TO_ONE,
    GenerationAspectRatio.THREE_TO_FOUR,
    GenerationAspectRatio.NINE_TO_SIXTEEN,
    GenerationAspectRatio.FOUR_TO_THREE,
    GenerationAspectRatio.SIXTEEN_TO_NINE,
  ],
  supportedImageSizes: IMAGE_SIZE_OPTIONS,
  defaultImageSize: GenerationImageSize.SQUARE_HD,
  defaultAspectRatio: GenerationAspectRatio.ONE_TO_ONE,
  defaultResolution: GenerationResolution.ONE_K,
  supportsReferenceImages: true,
  requiresReferenceImages: true,
  maxReferenceImages: 1,
  maxPromptLength: 5000,
  kieModelId: 'ideogram/character',
};

export const QWEN_IMAGE_EDIT_CONFIG: ModelConfig = {
  id: GenerationModel.QWEN_IMAGE_EDIT,
  displayName: 'Qwen Image Edit',
  description: 'Qwen image editing with KIE image-size presets.',
  promptKey: 'nanobanana',
  providers: ['kie'],
  preferredProvider: 'kie',
  supportedResolutions: [GenerationResolution.ONE_K],
  supportedQualities: [],
  supportedAspectRatios: [
    GenerationAspectRatio.ONE_TO_ONE,
    GenerationAspectRatio.THREE_TO_FOUR,
    GenerationAspectRatio.NINE_TO_SIXTEEN,
    GenerationAspectRatio.FOUR_TO_THREE,
    GenerationAspectRatio.SIXTEEN_TO_NINE,
  ],
  supportedImageSizes: IMAGE_SIZE_OPTIONS,
  defaultImageSize: GenerationImageSize.LANDSCAPE_4_3,
  defaultAspectRatio: GenerationAspectRatio.FOUR_TO_THREE,
  defaultResolution: GenerationResolution.ONE_K,
  supportsReferenceImages: true,
  requiresReferenceImages: true,
  maxReferenceImages: 1,
  maxPromptLength: 2000,
  kieModelId: 'qwen/image-edit',
};

export const Z_IMAGE_CONFIG: ModelConfig = {
  id: GenerationModel.Z_IMAGE,
  displayName: 'Z-Image',
  description: 'Z-Image text generation with a compact ratio set.',
  promptKey: 'nanobanana',
  providers: ['kie'],
  preferredProvider: 'kie',
  supportedResolutions: [GenerationResolution.ONE_K],
  supportedQualities: [],
  supportedAspectRatios: [
    GenerationAspectRatio.ONE_TO_ONE,
    GenerationAspectRatio.FOUR_TO_THREE,
    GenerationAspectRatio.THREE_TO_FOUR,
    GenerationAspectRatio.SIXTEEN_TO_NINE,
    GenerationAspectRatio.NINE_TO_SIXTEEN,
  ],
  defaultAspectRatio: GenerationAspectRatio.ONE_TO_ONE,
  defaultResolution: GenerationResolution.ONE_K,
  maxPromptLength: 1000,
  kieModelId: 'z-image',
};

export const TOPAZ_IMAGE_UPSCALE_CONFIG: ModelConfig = {
  id: GenerationModel.TOPAZ_IMAGE_UPSCALE,
  displayName: 'Topaz Image Upscale',
  description:
    'Specialist 2x or 4x image upscaling that preserves the source aspect ratio.',
  promptKey: 'nanobanana',
  providers: ['kie'],
  preferredProvider: 'kie',
  supportedResolutions: [
    GenerationResolution.TWO_K,
    GenerationResolution.FOUR_K,
  ],
  supportedQualities: [],
  supportedAspectRatios: [GenerationAspectRatio.AUTO],
  defaultAspectRatio: GenerationAspectRatio.AUTO,
  defaultResolution: GenerationResolution.FOUR_K,
  supportsReferenceImages: true,
  requiresReferenceImages: true,
  maxReferenceImages: 1,
  maxPromptLength: 200,
  kieModelId: 'topaz/image-upscale',
};

export const ALL_MODELS: ModelConfig[] = [
  GROK_IMAGINE_CONFIG,
  GPT_IMAGE_2_5_CONFIG,
  GPT_IMAGE_2_5_SUNBURST_CONFIG,
  GPT_IMAGE_2_CONFIG,
  NANO_BANANA_PRO_CONFIG,
  SEEDREAM_5_PRO_CONFIG,
  SEEDREAM_4_5_CONFIG,
  FLUX_2_CONFIG,
  NANO_BANANA_CONFIG,
  SEEDREAM_API_CONFIG,
  FOUR_O_IMAGE_CONFIG,
  FLUX_1_KONTEXT_CONFIG,
  IMAGEN_4_CONFIG,
  IDEOGRAM_V3_CONFIG,
  IDEOGRAM_CHARACTER_CONFIG,
  QWEN_IMAGE_EDIT_CONFIG,
  Z_IMAGE_CONFIG,
  NANO_BANANA_2_CONFIG,
];

export const MODELS_BY_ID: Record<GenerationModel, ModelConfig> = {
  [GenerationModel.GROK_IMAGINE]: GROK_IMAGINE_CONFIG,
  [GenerationModel.GPT_IMAGE_2_5]: GPT_IMAGE_2_5_CONFIG,
  [GenerationModel.GPT_IMAGE_2_5_SUNBURST]: GPT_IMAGE_2_5_SUNBURST_CONFIG,
  [GenerationModel.GPT_IMAGE_2]: GPT_IMAGE_2_CONFIG,
  [GenerationModel.NANO_BANANA_PRO]: NANO_BANANA_PRO_CONFIG,
  [GenerationModel.SEEDREAM_5_PRO]: SEEDREAM_5_PRO_CONFIG,
  [GenerationModel.SEEDREAM_4_5]: SEEDREAM_4_5_CONFIG,
  [GenerationModel.FLUX_2]: FLUX_2_CONFIG,
  [GenerationModel.NANO_BANANA]: NANO_BANANA_CONFIG,
  [GenerationModel.SEEDREAM_API]: SEEDREAM_API_CONFIG,
  [GenerationModel.FOUR_O_IMAGE]: FOUR_O_IMAGE_CONFIG,
  [GenerationModel.FLUX_1_KONTEXT]: FLUX_1_KONTEXT_CONFIG,
  [GenerationModel.IMAGEN_4]: IMAGEN_4_CONFIG,
  [GenerationModel.IDEOGRAM_V3]: IDEOGRAM_V3_CONFIG,
  [GenerationModel.IDEOGRAM_CHARACTER]: IDEOGRAM_CHARACTER_CONFIG,
  [GenerationModel.QWEN_IMAGE_EDIT]: QWEN_IMAGE_EDIT_CONFIG,
  [GenerationModel.Z_IMAGE]: Z_IMAGE_CONFIG,
  [GenerationModel.NANO_BANANA_2]: NANO_BANANA_2_CONFIG,
  [GenerationModel.TOPAZ_IMAGE_UPSCALE]: TOPAZ_IMAGE_UPSCALE_CONFIG,
};

/** Maps prompt brand key → default (latest) model config for that brand. */
export const MODELS_BY_PROMPT_KEY: Record<PromptModelKey, ModelConfig> = {
  nanobanana: NANO_BANANA_PRO_CONFIG,
  gptimage: GPT_IMAGE_2_5_CONFIG,
};

export function getModelConfig(modelId: GenerationModel): ModelConfig | null {
  return MODELS_BY_ID[modelId] ?? null;
}

export function getModelConfigByPromptKey(
  key: PromptModelKey
): ModelConfig | null {
  return MODELS_BY_PROMPT_KEY[key] ?? null;
}

export function isGptImage25Model(model: GenerationModel | string) {
  return (
    model === GenerationModel.GPT_IMAGE_2_5 ||
    model === GenerationModel.GPT_IMAGE_2_5_SUNBURST
  );
}

export function isGempix2Model(model: GenerationModel | string) {
  return (
    model === GenerationModel.NANO_BANANA_2 ||
    model === GenerationModel.NANO_BANANA_PRO
  );
}

export function getImageModelsForWorkflow(
  workflow: ImageGenerationWorkflowType
) {
  const pricedModels =
    workflow === 'image-editor'
      ? [
          GenerationModel.GROK_IMAGINE,
          GenerationModel.GPT_IMAGE_2_5,
          GenerationModel.GPT_IMAGE_2_5_SUNBURST,
          GenerationModel.GPT_IMAGE_2,
          GenerationModel.NANO_BANANA_2,
          GenerationModel.NANO_BANANA_PRO,
        ]
      : [
          GenerationModel.GROK_IMAGINE,
          GenerationModel.GPT_IMAGE_2_5,
          GenerationModel.GPT_IMAGE_2_5_SUNBURST,
          GenerationModel.GPT_IMAGE_2,
          GenerationModel.NANO_BANANA_2,
          GenerationModel.NANO_BANANA_PRO,
          GenerationModel.NANO_BANANA,
        ];

  return pricedModels
    .map((model) => getModelConfig(model))
    .filter((config): config is ModelConfig => Boolean(config));
}

export function supportsResolution(
  model: GenerationModel,
  resolution: GenerationResolution
): boolean {
  return (
    getModelConfig(model)?.supportedResolutions.includes(resolution) ?? false
  );
}

export function supportsQuality(
  model: GenerationModel,
  quality: GenerationQuality
): boolean {
  const config = getModelConfig(model);
  if (!config || config.supportedQualities.length === 0) return false;
  return config.supportedQualities.includes(quality);
}

export function supportsAspectRatio(
  model: GenerationModel,
  aspectRatio: GenerationAspectRatio
): boolean {
  return (
    getModelConfig(model)?.supportedAspectRatios.includes(aspectRatio) ?? false
  );
}

export function supportsImageSize(
  model: GenerationModel,
  imageSize: GenerationImageSize
): boolean {
  return (
    getModelConfig(model)?.supportedImageSizes?.includes(imageSize) ?? false
  );
}

export function supportsBackground(
  model: GenerationModel,
  background: GenerationBackground
): boolean {
  return (
    getModelConfig(model)?.supportedBackgrounds?.includes(background) ?? false
  );
}

export function supportsModelCombination(
  model: GenerationModel,
  resolution: GenerationResolution,
  aspectRatio: GenerationAspectRatio
): boolean {
  if (isGptImage25Model(model)) {
    return (
      resolution === GenerationResolution.ONE_K ||
      ![
        GenerationAspectRatio.TWENTY_SEVEN_TO_SIXTEEN,
        GenerationAspectRatio.SIXTEEN_TO_TWENTY_SEVEN,
        GenerationAspectRatio.NINE_TO_EIGHT,
        GenerationAspectRatio.EIGHT_TO_NINE,
      ].includes(aspectRatio)
    );
  }

  if (model !== GenerationModel.GPT_IMAGE_2) return true;

  if (
    aspectRatio === GenerationAspectRatio.AUTO &&
    resolution !== GenerationResolution.ONE_K
  ) {
    return false;
  }

  if (
    aspectRatio === GenerationAspectRatio.ONE_TO_ONE &&
    resolution === GenerationResolution.FOUR_K
  ) {
    return false;
  }

  if (
    resolution !== GenerationResolution.ONE_K &&
    [
      GenerationAspectRatio.FIVE_TO_FOUR,
      GenerationAspectRatio.FOUR_TO_FIVE,
      GenerationAspectRatio.THREE_TO_ONE,
      GenerationAspectRatio.ONE_TO_THREE,
      GenerationAspectRatio.NINE_TO_TWENTY_ONE,
    ].includes(aspectRatio)
  ) {
    return false;
  }

  return true;
}

// ---------------------------------------------------------------------------
// Resolution mappings (used by providers for pixel-level sizing)
// ---------------------------------------------------------------------------

export interface ResolutionMapping {
  resolution: GenerationResolution;
  aspectRatio: GenerationAspectRatio;
  width: number;
  height: number;
}

/**
 * Nano Banana: single tier (1K only), resolution auto-determined.
 * Approximate 1024px on longest side.
 */
export const NANO_BANANA_RESOLUTIONS: ResolutionMapping[] = [
  {
    resolution: GenerationResolution.ONE_K,
    aspectRatio: GenerationAspectRatio.ONE_TO_ONE,
    width: 1024,
    height: 1024,
  },
  {
    resolution: GenerationResolution.ONE_K,
    aspectRatio: GenerationAspectRatio.FOUR_TO_THREE,
    width: 1024,
    height: 768,
  },
  {
    resolution: GenerationResolution.ONE_K,
    aspectRatio: GenerationAspectRatio.THREE_TO_TWO,
    width: 1024,
    height: 683,
  },
  {
    resolution: GenerationResolution.ONE_K,
    aspectRatio: GenerationAspectRatio.SIXTEEN_TO_NINE,
    width: 1024,
    height: 576,
  },
  {
    resolution: GenerationResolution.ONE_K,
    aspectRatio: GenerationAspectRatio.THREE_TO_FOUR,
    width: 768,
    height: 1024,
  },
  {
    resolution: GenerationResolution.ONE_K,
    aspectRatio: GenerationAspectRatio.TWO_TO_THREE,
    width: 683,
    height: 1024,
  },
  {
    resolution: GenerationResolution.ONE_K,
    aspectRatio: GenerationAspectRatio.NINE_TO_SIXTEEN,
    width: 576,
    height: 1024,
  },
];

/**
 * Nano Banana 2 (Gemini 3.1 Flash): 1K, 2K, 4K
 */
export const NANO_BANANA_2_RESOLUTIONS: ResolutionMapping[] = [
  // 1K
  {
    resolution: GenerationResolution.ONE_K,
    aspectRatio: GenerationAspectRatio.ONE_TO_ONE,
    width: 1024,
    height: 1024,
  },
  {
    resolution: GenerationResolution.ONE_K,
    aspectRatio: GenerationAspectRatio.FOUR_TO_THREE,
    width: 1024,
    height: 768,
  },
  {
    resolution: GenerationResolution.ONE_K,
    aspectRatio: GenerationAspectRatio.THREE_TO_TWO,
    width: 1024,
    height: 683,
  },
  {
    resolution: GenerationResolution.ONE_K,
    aspectRatio: GenerationAspectRatio.SIXTEEN_TO_NINE,
    width: 1024,
    height: 576,
  },
  {
    resolution: GenerationResolution.ONE_K,
    aspectRatio: GenerationAspectRatio.THREE_TO_FOUR,
    width: 768,
    height: 1024,
  },
  {
    resolution: GenerationResolution.ONE_K,
    aspectRatio: GenerationAspectRatio.TWO_TO_THREE,
    width: 683,
    height: 1024,
  },
  {
    resolution: GenerationResolution.ONE_K,
    aspectRatio: GenerationAspectRatio.NINE_TO_SIXTEEN,
    width: 576,
    height: 1024,
  },
  // 2K
  {
    resolution: GenerationResolution.TWO_K,
    aspectRatio: GenerationAspectRatio.ONE_TO_ONE,
    width: 2048,
    height: 2048,
  },
  {
    resolution: GenerationResolution.TWO_K,
    aspectRatio: GenerationAspectRatio.FOUR_TO_THREE,
    width: 2048,
    height: 1536,
  },
  {
    resolution: GenerationResolution.TWO_K,
    aspectRatio: GenerationAspectRatio.THREE_TO_TWO,
    width: 2048,
    height: 1365,
  },
  {
    resolution: GenerationResolution.TWO_K,
    aspectRatio: GenerationAspectRatio.SIXTEEN_TO_NINE,
    width: 2048,
    height: 1152,
  },
  {
    resolution: GenerationResolution.TWO_K,
    aspectRatio: GenerationAspectRatio.THREE_TO_FOUR,
    width: 1536,
    height: 2048,
  },
  {
    resolution: GenerationResolution.TWO_K,
    aspectRatio: GenerationAspectRatio.TWO_TO_THREE,
    width: 1365,
    height: 2048,
  },
  {
    resolution: GenerationResolution.TWO_K,
    aspectRatio: GenerationAspectRatio.NINE_TO_SIXTEEN,
    width: 1152,
    height: 2048,
  },
  // 4K
  {
    resolution: GenerationResolution.FOUR_K,
    aspectRatio: GenerationAspectRatio.ONE_TO_ONE,
    width: 3840,
    height: 3840,
  },
  {
    resolution: GenerationResolution.FOUR_K,
    aspectRatio: GenerationAspectRatio.FOUR_TO_THREE,
    width: 3840,
    height: 2880,
  },
  {
    resolution: GenerationResolution.FOUR_K,
    aspectRatio: GenerationAspectRatio.THREE_TO_TWO,
    width: 3840,
    height: 2560,
  },
  {
    resolution: GenerationResolution.FOUR_K,
    aspectRatio: GenerationAspectRatio.SIXTEEN_TO_NINE,
    width: 3840,
    height: 2160,
  },
  {
    resolution: GenerationResolution.FOUR_K,
    aspectRatio: GenerationAspectRatio.THREE_TO_FOUR,
    width: 2880,
    height: 3840,
  },
  {
    resolution: GenerationResolution.FOUR_K,
    aspectRatio: GenerationAspectRatio.TWO_TO_THREE,
    width: 2560,
    height: 3840,
  },
  {
    resolution: GenerationResolution.FOUR_K,
    aspectRatio: GenerationAspectRatio.NINE_TO_SIXTEEN,
    width: 2160,
    height: 3840,
  },
];

/**
 * Nano Banana Pro (Gemini 3 Pro): 1K, 2K, 4K
 */
export const NANO_BANANA_PRO_RESOLUTIONS: ResolutionMapping[] = [
  // 1K
  {
    resolution: GenerationResolution.ONE_K,
    aspectRatio: GenerationAspectRatio.ONE_TO_ONE,
    width: 1024,
    height: 1024,
  },
  {
    resolution: GenerationResolution.ONE_K,
    aspectRatio: GenerationAspectRatio.FOUR_TO_THREE,
    width: 1024,
    height: 768,
  },
  {
    resolution: GenerationResolution.ONE_K,
    aspectRatio: GenerationAspectRatio.THREE_TO_TWO,
    width: 1024,
    height: 683,
  },
  {
    resolution: GenerationResolution.ONE_K,
    aspectRatio: GenerationAspectRatio.SIXTEEN_TO_NINE,
    width: 1024,
    height: 576,
  },
  {
    resolution: GenerationResolution.ONE_K,
    aspectRatio: GenerationAspectRatio.THREE_TO_FOUR,
    width: 768,
    height: 1024,
  },
  {
    resolution: GenerationResolution.ONE_K,
    aspectRatio: GenerationAspectRatio.TWO_TO_THREE,
    width: 683,
    height: 1024,
  },
  {
    resolution: GenerationResolution.ONE_K,
    aspectRatio: GenerationAspectRatio.NINE_TO_SIXTEEN,
    width: 576,
    height: 1024,
  },
  // 2K
  {
    resolution: GenerationResolution.TWO_K,
    aspectRatio: GenerationAspectRatio.ONE_TO_ONE,
    width: 2048,
    height: 2048,
  },
  {
    resolution: GenerationResolution.TWO_K,
    aspectRatio: GenerationAspectRatio.FOUR_TO_THREE,
    width: 2048,
    height: 1536,
  },
  {
    resolution: GenerationResolution.TWO_K,
    aspectRatio: GenerationAspectRatio.THREE_TO_TWO,
    width: 2048,
    height: 1365,
  },
  {
    resolution: GenerationResolution.TWO_K,
    aspectRatio: GenerationAspectRatio.SIXTEEN_TO_NINE,
    width: 2048,
    height: 1152,
  },
  {
    resolution: GenerationResolution.TWO_K,
    aspectRatio: GenerationAspectRatio.THREE_TO_FOUR,
    width: 1536,
    height: 2048,
  },
  {
    resolution: GenerationResolution.TWO_K,
    aspectRatio: GenerationAspectRatio.TWO_TO_THREE,
    width: 1365,
    height: 2048,
  },
  {
    resolution: GenerationResolution.TWO_K,
    aspectRatio: GenerationAspectRatio.NINE_TO_SIXTEEN,
    width: 1152,
    height: 2048,
  },
  // 4K
  {
    resolution: GenerationResolution.FOUR_K,
    aspectRatio: GenerationAspectRatio.ONE_TO_ONE,
    width: 3840,
    height: 3840,
  },
  {
    resolution: GenerationResolution.FOUR_K,
    aspectRatio: GenerationAspectRatio.FOUR_TO_THREE,
    width: 3840,
    height: 2880,
  },
  {
    resolution: GenerationResolution.FOUR_K,
    aspectRatio: GenerationAspectRatio.THREE_TO_TWO,
    width: 3840,
    height: 2560,
  },
  {
    resolution: GenerationResolution.FOUR_K,
    aspectRatio: GenerationAspectRatio.SIXTEEN_TO_NINE,
    width: 3840,
    height: 2160,
  },
  {
    resolution: GenerationResolution.FOUR_K,
    aspectRatio: GenerationAspectRatio.THREE_TO_FOUR,
    width: 2880,
    height: 3840,
  },
  {
    resolution: GenerationResolution.FOUR_K,
    aspectRatio: GenerationAspectRatio.TWO_TO_THREE,
    width: 2560,
    height: 3840,
  },
  {
    resolution: GenerationResolution.FOUR_K,
    aspectRatio: GenerationAspectRatio.NINE_TO_SIXTEEN,
    width: 2160,
    height: 3840,
  },
];

/**
 * GPT Image 2 (OpenAI): 1K, 2K, 4K
 */
export const GPT_IMAGE_2_RESOLUTIONS: ResolutionMapping[] = [
  // 1K
  {
    resolution: GenerationResolution.ONE_K,
    aspectRatio: GenerationAspectRatio.ONE_TO_ONE,
    width: 1024,
    height: 1024,
  },
  {
    resolution: GenerationResolution.ONE_K,
    aspectRatio: GenerationAspectRatio.FOUR_TO_THREE,
    width: 1024,
    height: 768,
  },
  {
    resolution: GenerationResolution.ONE_K,
    aspectRatio: GenerationAspectRatio.THREE_TO_TWO,
    width: 1248,
    height: 832,
  },
  {
    resolution: GenerationResolution.ONE_K,
    aspectRatio: GenerationAspectRatio.SIXTEEN_TO_NINE,
    width: 1360,
    height: 768,
  },
  {
    resolution: GenerationResolution.ONE_K,
    aspectRatio: GenerationAspectRatio.THREE_TO_FOUR,
    width: 768,
    height: 1024,
  },
  {
    resolution: GenerationResolution.ONE_K,
    aspectRatio: GenerationAspectRatio.TWO_TO_THREE,
    width: 832,
    height: 1248,
  },
  {
    resolution: GenerationResolution.ONE_K,
    aspectRatio: GenerationAspectRatio.NINE_TO_SIXTEEN,
    width: 768,
    height: 1360,
  },
  // 2K
  {
    resolution: GenerationResolution.TWO_K,
    aspectRatio: GenerationAspectRatio.ONE_TO_ONE,
    width: 2048,
    height: 2048,
  },
  {
    resolution: GenerationResolution.TWO_K,
    aspectRatio: GenerationAspectRatio.FOUR_TO_THREE,
    width: 2048,
    height: 1536,
  },
  {
    resolution: GenerationResolution.TWO_K,
    aspectRatio: GenerationAspectRatio.THREE_TO_TWO,
    width: 2496,
    height: 1664,
  },
  {
    resolution: GenerationResolution.TWO_K,
    aspectRatio: GenerationAspectRatio.SIXTEEN_TO_NINE,
    width: 2736,
    height: 1536,
  },
  {
    resolution: GenerationResolution.TWO_K,
    aspectRatio: GenerationAspectRatio.THREE_TO_FOUR,
    width: 1536,
    height: 2048,
  },
  {
    resolution: GenerationResolution.TWO_K,
    aspectRatio: GenerationAspectRatio.TWO_TO_THREE,
    width: 1664,
    height: 2496,
  },
  {
    resolution: GenerationResolution.TWO_K,
    aspectRatio: GenerationAspectRatio.NINE_TO_SIXTEEN,
    width: 1536,
    height: 2736,
  },
  // 4K
  {
    resolution: GenerationResolution.FOUR_K,
    aspectRatio: GenerationAspectRatio.ONE_TO_ONE,
    width: 3840,
    height: 3840,
  },
  {
    resolution: GenerationResolution.FOUR_K,
    aspectRatio: GenerationAspectRatio.FOUR_TO_THREE,
    width: 3840,
    height: 2880,
  },
  {
    resolution: GenerationResolution.FOUR_K,
    aspectRatio: GenerationAspectRatio.THREE_TO_TWO,
    width: 3840,
    height: 2560,
  },
  {
    resolution: GenerationResolution.FOUR_K,
    aspectRatio: GenerationAspectRatio.SIXTEEN_TO_NINE,
    width: 3840,
    height: 2160,
  },
  {
    resolution: GenerationResolution.FOUR_K,
    aspectRatio: GenerationAspectRatio.THREE_TO_FOUR,
    width: 2880,
    height: 3840,
  },
  {
    resolution: GenerationResolution.FOUR_K,
    aspectRatio: GenerationAspectRatio.TWO_TO_THREE,
    width: 2560,
    height: 3840,
  },
  {
    resolution: GenerationResolution.FOUR_K,
    aspectRatio: GenerationAspectRatio.NINE_TO_SIXTEEN,
    width: 2160,
    height: 3840,
  },
];

const RESOLUTIONS_BY_MODEL: Partial<
  Record<GenerationModel, ResolutionMapping[]>
> = {
  [GenerationModel.NANO_BANANA]: NANO_BANANA_RESOLUTIONS,
  [GenerationModel.NANO_BANANA_2]: NANO_BANANA_2_RESOLUTIONS,
  [GenerationModel.NANO_BANANA_PRO]: NANO_BANANA_PRO_RESOLUTIONS,
  [GenerationModel.GPT_IMAGE_2]: GPT_IMAGE_2_RESOLUTIONS,
};

export function getResolution(
  model: GenerationModel,
  resolution: GenerationResolution,
  aspectRatio: GenerationAspectRatio
): ResolutionMapping | null {
  const resolutions = RESOLUTIONS_BY_MODEL[model] ?? [];
  return (
    resolutions.find(
      (r) => r.resolution === resolution && r.aspectRatio === aspectRatio
    ) ?? null
  );
}
